import translate from '@translate';
import React, { FC } from 'react';
import Grid, { GridItem } from 'src/components/Grid';
import Select from 'src/components/Select';
import TextField from 'src/components/TextField';
import { ClaimType } from 'src/types/claim';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import DateTimePicker from 'src/components/DateTimePicker';
import AutocompleteTextField from '../../components/AutocompleteTextField';
import { Typography } from '@mui/material';
import colorOptionFce from '../../utils/colorOptionFce';
import Switch from '../../components/Switch';
import { sizes } from '../../themes/default';
import FormBlock from '../../components/FormBlock';
import { FormErrorsType, OnChangeType } from '../../types/global';
import { CLAIM_STATUS_ACCEPTED_PENDING_CLIENT_COMPENSATION } from 'skladon-types';
import { EditKeysType } from './constants';

export interface DetailPropsType {
    data: ClaimType | null;
    onChange: OnChangeType<EditKeysType>;
    editData: ClaimType;
    editErrors: FormErrorsType;
    newClaim: boolean;
    disableEdit: boolean;
    permissionEditAll: boolean;
    isAttributeEditDisabled: (attributeKey: string) => boolean;
    allowedStatuses: ConstantsCodeType[];
    claimStatusCodes: ConstantsCodeType[];
    claimReasonCodes: ConstantsCodeType[];
    claimsResolverCodes: ConstantsCodeType[];
    skladonUser?: number;
    currencyCodes: ConstantsCodeType[];
}

const Detail: FC<DetailPropsType> = (props) => {
    if (props.newClaim) {
        return (
            <FormBlock sx={{ marginTop: sizes.blocks_top_MG }}>
                <Grid cols={12}>
                    <GridItem cols={12}>
                        <Grid>
                            <GridItem cols={3}>
                                <TextField
                                    value={props.editData.ESHOP_CLAIM_ID}
                                    error={props.editErrors.ESHOP_CLAIM_ID}
                                    onChange={(value, error) => props.onChange('ESHOP_CLAIM_ID', value, error)}
                                    name="ESHOP_CLAIM_ID"
                                    label={translate('claimDetail.eshopClaimId')}
                                    fullWidth
                                    disabled={props.disableEdit}
                                />
                            </GridItem>
                            <GridItem cols={3}>
                                <Select
                                    options={props.claimReasonCodes}
                                    value={props.editData.CLAIM_CAUSE}
                                    error={props.editErrors.CLAIM_CAUSE}
                                    onChange={(value, error) => props.onChange('CLAIM_CAUSE', value, error)}
                                    name="CLAIM_CAUSE"
                                    label={translate('claimDetail.claimCause')}
                                    required
                                    fullWidth
                                    disabled={props.disableEdit}
                                />
                            </GridItem>
                            <GridItem cols={3}>
                                {props.editData.CLAIM_CAUSE === 'others' && (
                                    <TextField
                                        value={props.editData.CLAIM_CAUSE_OTHERS_TYPE}
                                        error={props.editErrors.CLAIM_CAUSE_OTHERS_TYPE}
                                        onChange={(value, error) => props.onChange('CLAIM_CAUSE_OTHERS_TYPE', value, error)}
                                        name="CLAIM_CAUSE_OTHERS_TYPE"
                                        label={translate('claimDetail.causeOtherTypes')}
                                        fullWidth
                                        required
                                        disabled={props.disableEdit}
                                    />
                                )}
                            </GridItem>
                            <GridItem cols={3}>
                                <Select
                                    options={props.claimsResolverCodes}
                                    value={props.editData.RESOLVER || ''}
                                    error={props.editErrors.RESOLVER}
                                    onChange={(value, error) => props.onChange('RESOLVER', value || '', error)}
                                    name="RESOLVER"
                                    label={translate('claimDetail.resolver')}
                                    fullWidth
                                    disabled={!props.permissionEditAll && !props.newClaim}
                                    required={!!props.skladonUser}
                                />
                            </GridItem>
                        </Grid>
                    </GridItem>
                    <GridItem cols={12}>
                        <Grid>
                            <GridItem cols={12}>
                                <AutocompleteTextField
                                    value={props.editData.DESCRIPTION}
                                    error={props.editErrors.DESCRIPTION}
                                    onChange={(value, error) => props.onChange('DESCRIPTION', value, error)}
                                    name="DESCRIPTION"
                                    label={translate('claimDetail.description')}
                                    fullWidth
                                    required
                                    multiline
                                    disabled={props.isAttributeEditDisabled('DESCRIPTION')}
                                    autoComplete="CLAIM_DESCRIPTION"
                                />
                            </GridItem>
                        </Grid>
                    </GridItem>
                    <GridItem cols={12}>
                        <Grid>
                            {props.skladonUser ? (
                                <GridItem cols={6}>
                                    <AutocompleteTextField
                                        value={props.editData.NOTE}
                                        error={props.editErrors.NOTE}
                                        onChange={(value, error) => props.onChange('NOTE', value || '', error)}
                                        name="NOTE"
                                        label={translate('claimDetail.note')}
                                        fullWidth
                                        // required
                                        multiline
                                        disabled={props.disableEdit}
                                        autoComplete="CLAIM_NOTE"
                                    />
                                </GridItem>
                            ) : null}
                            <GridItem cols={props.skladonUser ? 6 : 12}>
                                <TextField
                                    value={props.editData.ESHOP_NOTE}
                                    error={props.editErrors.ESHOP_NOTE}
                                    onChange={(value, error) => props.onChange('ESHOP_NOTE', value, error)}
                                    name="ESHOP_NOTE"
                                    label={translate('claimDetail.eshopNote')}
                                    fullWidth
                                    multiline
                                />
                            </GridItem>
                        </Grid>
                    </GridItem>
                </Grid>
            </FormBlock>
        );
    }
    return (
        <>
            <FormBlock sx={{ marginTop: sizes.blocks_top_MG }}>
                <Grid>
                    <GridItem cols={12}>
                        <Typography variant="h3" gutterBottom>
                            {translate('claimDetail.detail')}
                        </Typography>
                    </GridItem>
                    <GridItem cols={12}>
                        <Grid>
                            <GridItem cols={2}>
                                <TextField
                                    value={props.editData.FCN}
                                    name="FCN"
                                    label={translate('claimDetail.fcn')}
                                    fullWidth
                                    disabled
                                />
                            </GridItem>
                            <GridItem cols={2}>
                                <TextField
                                    value={props.editData.ESHOP_CLAIM_ID}
                                    error={props.editErrors.ESHOP_CLAIM_ID}
                                    onChange={(value, error) => props.onChange('ESHOP_CLAIM_ID', value, error)}
                                    name="ESHOP_CLAIM_ID"
                                    label={translate('claimDetail.eshopClaimId')}
                                    fullWidth
                                    disabled={props.disableEdit}
                                />
                            </GridItem>
                            <GridItem cols={2}>
                                <TextField
                                    value={props.editData.ORDER_ID != null ? String(props.editData.ORDER_ID) : ''}
                                    name="ORDER_ID"
                                    label={translate('claimDetail.orderId')}
                                    fullWidth
                                    disabled
                                />
                            </GridItem>
                            <GridItem cols={2}>
                                <TextField
                                    value={props.editData.TRACKING_NUMBER ?? ''}
                                    name="TRACKING_NUMBER"
                                    label={translate('claimDetail.trackingNumber')}
                                    fullWidth
                                    disabled
                                />
                            </GridItem>
                            <GridItem cols={2}>
                                <DateTimePicker
                                    value={props.editData.CREATED_AT}
                                    label={translate('claimDetail.createdAt')}
                                    type="dateTime"
                                    // format="l"
                                    fullWidth
                                    disabled
                                />
                            </GridItem>
                            <GridItem cols={2}>
                                <Select
                                    options={props.claimsResolverCodes}
                                    value={props.editData.RESOLVER || ''}
                                    error={props.editErrors.RESOLVER}
                                    onChange={(value, error) => props.onChange('RESOLVER', value || '', error)}
                                    name="RESOLVER"
                                    label={translate('claimDetail.resolver')}
                                    fullWidth
                                    disabled={!props.permissionEditAll && !props.newClaim}
                                    required={!!props.skladonUser}
                                />
                            </GridItem>
                            <GridItem cols={2}>
                                <Select
                                    options={props.claimReasonCodes}
                                    value={props.editData.CLAIM_CAUSE}
                                    error={props.editErrors.CLAIM_CAUSE}
                                    onChange={(value, error) => props.onChange('CLAIM_CAUSE', value, error)}
                                    name="CLAIM_CAUSE"
                                    label={translate('claimDetail.claimCause')}
                                    required
                                    fullWidth
                                    disabled={props.disableEdit}
                                />
                            </GridItem>
                            {props.editData.CLAIM_CAUSE === 'others' && (
                                <GridItem cols={2}>
                                    <TextField
                                        value={props.editData.CLAIM_CAUSE_OTHERS_TYPE}
                                        error={props.editErrors.CLAIM_CAUSE_OTHERS_TYPE}
                                        onChange={(value, error) => props.onChange('CLAIM_CAUSE_OTHERS_TYPE', value, error)}
                                        name="CLAIM_CAUSE_OTHERS_TYPE"
                                        label={translate('claimDetail.causeOtherTypes')}
                                        fullWidth
                                        required
                                        disabled={props.disableEdit}
                                    />
                                </GridItem>
                            )}
                            <GridItem cols={4}>
                                <Select
                                    options={props.claimStatusCodes}
                                    editableOptions={props.allowedStatuses}
                                    value={props.editData.STATUS}
                                    error={props.editErrors.STATUS}
                                    onChange={(value, error) => props.onChange('STATUS', value, error)}
                                    name="STATUS"
                                    label={translate('claimDetail.status')}
                                    required
                                    fullWidth
                                    disabled={props.disableEdit}
                                    optionStyleFce={colorOptionFce}
                                />
                            </GridItem>
                            {!props.newClaim && props.editData.CLOSED_AT && ['accepted', 'rejected', 'storno'].includes(props.editData.STATUS || '') && (
                                <GridItem cols={2}>
                                    <DateTimePicker
                                        type="dateTime"
                                        value={props.editData?.CLOSED_AT}
                                        label={translate('claimDetail.closedAt')}
                                        fullWidth
                                        disabled
                                    />
                                </GridItem>
                            )}
                            {!props.newClaim && props.editData.INFORMATION_ADDED_AT && props.editData.STATUS === 'informationAdded' && (
                                <GridItem cols={2}>
                                    <DateTimePicker
                                        type="dateTime"
                                        value={props.editData?.INFORMATION_ADDED_AT}
                                        label={translate('claimDetail.informationAddedAt')}
                                        fullWidth
                                        disabled
                                    />
                                </GridItem>
                            )}
                            <GridItem
                                cols={2}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    alignSelf: 'stretch',
                                }}
                            >
                                <Switch
                                    value={!!props.editData.CCTV}
                                    onChange={(value) => props.onChange('CCTV', value ? 1 : 0, false)}
                                    label={translate('claimDetail.cctv')}
                                    disabled={props.disableEdit || !props.skladonUser}
                                />
                            </GridItem>
                        </Grid>
                    </GridItem>
                    <GridItem cols={12}>
                        <Grid>
                            <GridItem cols={12}>
                                <AutocompleteTextField
                                    value={props.editData.DESCRIPTION}
                                    error={props.editErrors.DESCRIPTION}
                                    onChange={(value, error) => props.onChange('DESCRIPTION', value, error)}
                                    name="DESCRIPTION"
                                    label={translate('claimDetail.description')}
                                    fullWidth
                                    required
                                    multiline
                                    disabled={props.isAttributeEditDisabled('DESCRIPTION')}
                                    autoComplete="CLAIM_DESCRIPTION"
                                />
                            </GridItem>
                        </Grid>
                    </GridItem>
                    <GridItem cols={12}>
                        <Grid>
                            <GridItem cols={12}>
                                {props.data?.STATUS !== 'received' && (
                                    <AutocompleteTextField
                                        value={props.editData.RESOLUTION_TEXT}
                                        error={props.editErrors.RESOLUTION_TEXT}
                                        onChange={(value, error) => props.onChange('RESOLUTION_TEXT', value, error)}
                                        name="RESOLUTION_TEXT"
                                        label={translate('claimDetail.resolution')}
                                        fullWidth
                                        // todo: zistit aka ma tu byt spravna podmienka
                                        // required={props.editData.STATUS !== 'storno'}
                                        multiline
                                        disabled={props.disableEdit || !props.skladonUser}
                                        autoComplete="CLAIM_RESOLUTION_TEXT"
                                    />
                                )}
                            </GridItem>
                        </Grid>
                    </GridItem>
                    <GridItem cols={12}>
                        <Grid>
                            {props.skladonUser ? (
                                <GridItem cols={6}>
                                    <AutocompleteTextField
                                        value={props.editData.NOTE}
                                        error={props.editErrors.NOTE}
                                        onChange={(value, error) => props.onChange('NOTE', value || '', error)}
                                        name="NOTE"
                                        label={translate('claimDetail.note')}
                                        fullWidth
                                        // required
                                        multiline
                                        disabled={props.disableEdit}
                                        autoComplete="CLAIM_NOTE"
                                    />
                                </GridItem>
                            ) : null}
                            <GridItem cols={props.skladonUser ? 6 : 12}>
                                <TextField
                                    value={props.editData.ESHOP_NOTE}
                                    error={props.editErrors.ESHOP_NOTE}
                                    onChange={(value, error) => props.onChange('ESHOP_NOTE', value, error)}
                                    name="ESHOP_NOTE"
                                    label={translate('claimDetail.eshopNote')}
                                    fullWidth
                                    multiline
                                />
                            </GridItem>
                        </Grid>
                    </GridItem>
                </Grid>
            </FormBlock>
            <FormBlock sx={{ marginTop: sizes.blocks_top_MG }}>
                <Grid>
                    <GridItem cols={12}>
                        <Typography variant="h3" gutterBottom>
                            {translate('claimDetail.compensations')}
                        </Typography>
                    </GridItem>
                    <GridItem cols={12}>
                        <Grid>
                            <GridItem cols={2}>
                                <TextField
                                    value={props.editData.TRANSPORT_PRICE ?? ''}
                                    error={props.editErrors.TRANSPORT_PRICE}
                                    onChange={(value, error) => props.onChange('TRANSPORT_PRICE', value, error)}
                                    name="TRANSPORT_PRICE"
                                    label={translate('claimDetail.transportPrice')}
                                    fullWidth
                                    type="text"
                                    disabled={props.disableEdit || !props.skladonUser}
                                />
                            </GridItem>
                            <GridItem cols={2}>
                                <TextField
                                    value={props.editData.GOODS_PRICE ?? ''}
                                    error={props.editErrors.GOODS_PRICE}
                                    onChange={(value, error) => props.onChange('GOODS_PRICE', value, error)}
                                    name="GOODS_PRICE"
                                    label={translate('claimDetail.goodsPrice')}
                                    fullWidth
                                    type="text"
                                    disabled={props.disableEdit || !props.skladonUser}
                                />
                            </GridItem>
                            <GridItem cols={2}>
                                <TextField
                                    value={props.editData.TOTAL_SERVICE_PRICE ?? ''}
                                    error={props.editErrors.TOTAL_SERVICE_PRICE}
                                    onChange={(value, error) => props.onChange('TOTAL_SERVICE_PRICE', value, error)}
                                    name="TOTAL_SERVICE_PRICE"
                                    label={translate('claimDetail.totalServicePrice')}
                                    fullWidth
                                    type="text"
                                    disabled={props.disableEdit || !props.skladonUser}
                                />
                            </GridItem>
                            <GridItem cols={2}>
                                <Select
                                    options={props.currencyCodes}
                                    value={props.editData.COMPENSATION_CURRENCY}
                                    error={props.editErrors.COMPENSATION_CURRENCY}
                                    onChange={(value, error) => props.onChange('COMPENSATION_CURRENCY', value, error)}
                                    name="COMPENSATION_CURRENCY"
                                    label={translate('claimDetail.compensationCurrency')}
                                    fullWidth
                                    disabled={props.disableEdit || !props.skladonUser}
                                />
                            </GridItem>
                            {props.skladonUser ? (
                                <>
                                    <GridItem cols={2}>
                                        <TextField
                                            value={props.editData.CARRIER_INVOICE_REFERENCE}
                                            error={props.editErrors.CARRIER_INVOICE_REFERENCE}
                                            onChange={(value, error) => props.onChange('CARRIER_INVOICE_REFERENCE', value, error)}
                                            name="CARRIER_INVOICE_REFERENCE"
                                            label={translate('claimDetail.carrierInvoiceReference')}
                                            fullWidth
                                            disabled={props.disableEdit || !props.skladonUser}
                                            maxLength={30}
                                        />
                                    </GridItem>
                                    <GridItem cols={2}>
                                        <DateTimePicker
                                            value={props.editData.ISSUES_MONTH_ORIGIN}
                                            onChange={(value, error) => props.onChange('ISSUES_MONTH_ORIGIN', value, error)}
                                            label={translate('claimDetail.issuesMonthOrigin')}
                                            name="ISSUES_MONTH_ORIGIN"
                                            error={props.editErrors.ISSUES_MONTH_ORIGIN}
                                            required={
                                                props.editData.STATUS === CLAIM_STATUS_ACCEPTED_PENDING_CLIENT_COMPENSATION
                                            }
                                            type="date"
                                            fullWidth
                                            disabled={props.disableEdit || !props.skladonUser}
                                        />
                                    </GridItem>
                                </>
                            ) : null}
                        </Grid>
                    </GridItem>
                </Grid>
            </FormBlock>
        </>
    );
};

export default Detail;
